import { FolderItemType, ShareRepresentation, ShareStatus } from '@common/api';
import { Button } from '@components/button';
import { ContextMenu } from '@components/context-menu/ContextMenu';
import { DateDifference } from '@components/date-difference/DateDifference';
import { Archive, Close, TrackStatus, UntrackStatus, Tick, RestartStatus } from '@components/icons/FarseerIcons';
import { FlexRow, FlexCol } from '@components/layout/Flex';
import { Status } from '@components/Status';
import {
    PopoverCell,
    TableSheetCellWidget,
    TableSheetDataCell,
    TableSheetIgnoreDragContainer,
} from '@components/table-sheet';
import { Text } from '@components/typography/Typography';
import { useGroups } from '@data/hooks/usersAndGroups';
import { shareIsTracked, useShareActions } from '@data/share/shareActions';
//import { isUserInGroup } from '@data/user/user';
import { useToggle } from '@hooks/useToggle';
import { useI18nContext } from '@i18n/i18n-react';
import { useAuth } from '@providers/AuthProvider';
import { Update } from '@use-gpu/state';
import { CSSProperties, ReactNode, useMemo, useRef } from 'react';

type ShareStatusMenuItemsProps = {
    items: ShareRepresentation[];
    divider?: boolean;
    onChange?: (updates: Update<ShareRepresentation>[]) => void;
};

const ShareStatusTracking = (props: ShareStatusMenuItemsProps) => {
    const { onChange } = props;

    const { LL } = useI18nContext();
    const { user } = useAuth();

    const handleTrack = () => {
        if (!user) return;

        onChange?.([
            {
                status: ShareStatus.Shared,
                approverUserId: user.id,
                approverUserGroupId: null,
            },
        ]);
    };

    return (
        <FlexCol $gap={6} style={{ width: 240 }}>
            <Text style={{ fontWeight: 500 }}>{LL.STATUS_TRACKING()}</Text>
            <Text $muted>{LL.STATUS_TRACKING_MESSAGE()}</Text>
            <Button title={LL.TRACK_STATUS()} onClick={handleTrack} />
        </FlexCol>
    );
};

export const ShareStatusMenuItems = (props: ShareStatusMenuItemsProps & { items: ShareRepresentation[] }) => {
    const { items, divider, onChange } = props;
    const { LL } = useI18nContext();
    const { user } = useAuth();
    const groups = useGroups();
    const {
        api: { updateStatus },
    } = useShareActions();

    if (!user || !groups || items.length === 0) return null;

    //const isSuperUser = user.superUser;

    const allAreSheets = items.every((i) => i.itemNode.type === FolderItemType.Sheet);
    const allAreFolders = items.every((i) => i.itemNode.type === FolderItemType.Folder);
    //const allAreApprover = items.every(
    //    (i) =>
    //        i.approverUserId === user.id ||
    //        (i.approverUserGroupId && isUserInGroup(user.id, i.approverUserGroupId, groups))
    //);

    //const canEdit = isSuperUser || allAreApprover;

    const allTracked = items.every((i) => shareIsTracked(i));
    const noneTracked = items.every((i) => !shareIsTracked(i));

    const allArchived = items.every((i) => i.status === ShareStatus.Archived);
    const allApproved = items.every((i) => i.status === ShareStatus.Approved);
    const allInProgress = items.every((i) =>
        [ShareStatus.InProgress, ShareStatus.Submitted, ShareStatus.Shared].includes(i.status as ShareStatus)
    );

    const ids = items.map((i) => i.id);

    const handleUpdateStatus = (status: ShareStatus) => ids.forEach((id) => updateStatus({ id, status }));

    const handleTrackingToggle = () => {
        onChange?.(
            items.map((item) => {
                const status = allTracked ? null : ShareStatus.Shared;
                let approverUserId = item.approverUserId;
                let approverUserGroupId = item.approverUserGroupId;
                if (noneTracked) approverUserId = user!.id;
                else if (allTracked) approverUserId = approverUserGroupId = null;

                return {
                    status,
                    approverUserId,
                    approverUserGroupId,
                };
            })
        );
    };

    const menuItems: ReactNode[] = [];

    if (noneTracked && !allAreFolders) {
        menuItems.push(
            <ContextMenu.Item
                key="track"
                item={{
                    label: LL.TRACK_STATUS(),
                    icon: TrackStatus,
                    action: handleTrackingToggle,
                }}
            />
        );
    }

    if (allTracked && !allArchived) {
        menuItems.push(
            <ContextMenu.Item
                key="untrack"
                item={{
                    icon: UntrackStatus,
                    label: LL.STOP_TRACKING(),
                    action: handleTrackingToggle,
                }}
            />
        );
    }

    if (allAreSheets && allTracked && allInProgress) {
        menuItems.push(
            <ContextMenu.Item
                key="approve"
                item={{
                    label: LL.APPROVE(),
                    icon: Tick,
                    action: () => handleUpdateStatus(ShareStatus.Approved),
                }}
            />
        );
    }

    if (allAreSheets && allTracked && items.every((i) => i.status === ShareStatus.Submitted)) {
        menuItems.push(
            <ContextMenu.Item
                key="disapprove"
                item={{
                    label: LL.DISAPPROVE(),
                    icon: Close,
                    action: () => handleUpdateStatus(ShareStatus.Shared),
                }}
            />
        );
    }

    if (allAreSheets && allTracked && allApproved) {
        menuItems.push(
            <>
                <ContextMenu.Item
                    key="archive"
                    item={{
                        icon: Archive,
                        label: LL.ARCHIVE(),
                        action: () => handleUpdateStatus(ShareStatus.Archived),
                    }}
                />
                <ContextMenu.Item
                    key="unarchive"
                    item={{
                        icon: RestartStatus,
                        label: LL.RESTART_TRACKING(),
                        action: () => handleUpdateStatus(ShareStatus.Shared),
                    }}
                />
            </>
        );
    }

    if (allAreSheets && allTracked && allArchived) {
        menuItems.push(
            <ContextMenu.Item
                key="unarchive"
                item={{
                    icon: RestartStatus,
                    label: LL.RESTART_TRACKING(),
                    action: () => handleUpdateStatus(ShareStatus.Shared),
                }}
            />
        );
    }

    if (divider && menuItems.length) {
        menuItems.push(<ContextMenu.Divider key="d-divider" />);
    }

    return <>{menuItems}</>;
};

type ShareStatusWidgetProps = {
    item: ShareRepresentation;
    wrapperStyle: CSSProperties;

    onChange?: (update: Update<ShareRepresentation>) => void;
    onClose?: () => void;
};

export const ShareStatusWidget = (props: ShareStatusWidgetProps) => {
    const { item, wrapperStyle, onChange, onClose } = props;
    const [open, api] = useToggle(true);
    const anchorRef = useRef(null);
    const menuState = useMemo(() => ({ open, closeMenu: api.off, openMenu: api.on, setOpen: api.set }), [api, open]);

    const handleChange = (updates: Update<ShareRepresentation>[]) => onChange?.(updates[0]);

    if (!item.status) {
        return (
            <PopoverCell wrapperStyle={wrapperStyle} onClose={onClose}>
                <ShareStatusTracking items={[item]} onChange={handleChange} />
            </PopoverCell>
        );
    }

    return (
        <TableSheetCellWidget>
            <TableSheetIgnoreDragContainer>
                <div ref={anchorRef} style={wrapperStyle} />
                <ContextMenu {...menuState} anchorRef={anchorRef} placement="bottom-start" offsetMain={6}>
                    <ShareStatusMenuItems items={[item]} onChange={handleChange} />
                </ContextMenu>
            </TableSheetIgnoreDragContainer>
        </TableSheetCellWidget>
    );
};

export const makeShareStatusCell = (): TableSheetDataCell<
    ShareRepresentation,
    ShareRepresentation,
    ShareRepresentation,
    Update<ShareRepresentation>
> => ({
    input: ({ value: share, wrapperStyle, onChange, onClose }) => (
        <ShareStatusWidget item={share} wrapperStyle={wrapperStyle} onClose={onClose} onChange={onChange} />
    ),
});

export const formatAssignmentStatus = (share: ShareRepresentation) => {
    if (!share.status) return null;
    const { status, expirationTime } = share;

    return (
        <FlexRow $gap={4}>
            <Status status={status} />
            {expirationTime.active && (status === ShareStatus.InProgress || status === ShareStatus.Shared) && (
                <>
                    (<DateDifference date={expirationTime.value} />)
                </>
            )}
        </FlexRow>
    );
};
